import { useEffect, useState } from 'react';
import { Pestanas } from '../components/Pestanas';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip,
  ComposedChart, Bar, Line, CartesianGrid, Legend,
} from 'recharts';
import { Barras } from '../components/Barras';
import { COLOR_PERSONA, NEUTRO, CATEGORICO, porcentaje, dineroEje } from '../lib/graficas';
import { TopBar } from '../components/TopBar';
import { ChartCard } from '../components/ChartCard';
import { api } from '../lib/api';
import { useLive } from '../lib/useLive';
import { PeriodPicker, periodoQS } from '../components/PeriodPicker';
import { GaleriaContenido } from './Galeria';
import { usePermisos } from '../lib/permisos';
import { usePeriodoInicial } from '../lib/preferencias';

/**
 * Marketing — dinero real contra gasto real.
 *
 * LA DIFERENCIA CON "ANALÍTICA": ahí el ingreso es una ESTIMACIÓN a precio de
 * lista del catálogo, porque en la base clínica no hay pagos capturados. Aquí
 * es lo que se cobró y lo que se pagó de pauta. Es la única pantalla del HUB
 * cuyos números se pueden llevar a una junta de presupuesto.
 *
 * LO QUE ESTA PANTALLA NO PUEDE HACER: fingir que la atribución está completa.
 * Hoy la mayoría del dinero entra sin anuncio asociado. Ese hueco va arriba,
 * en grande y antes que el ROAS — porque un ROAS bonito calculado sobre una
 * fracción del dinero es exactamente el número con el que alguien apagaría la
 * pauta que sí funciona.
 */

const GOLD = '#AC996C', GOLD_DARK = '#8b7355', GOLD_LIGHT = '#cbb88e';
const VERDE = '#5b8a72', ROJO = '#a9745b';
const money = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);
/** Un día legible ("5 dic 2025"); lo que llegue como fecha ISO del servidor ("2025-12-05T00:00:00.000Z") se lee como ese día. */
const diaTxt = (x: string | null | undefined) => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(x || ''));
  return m ? new Date(m[1] + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : String(x || '');
};

interface RoasRow {
  anuncio: string; campana: string | null; gasto: number; ingreso: number;
  ventas: number; conversaciones: number; roas: number | null; costoPorVenta: number | null;
}
interface Resp {
  sinDatos?: boolean; motivo?: string; queHacer?: string;
  periodo: { label: string; all: boolean };
  ingreso: { total: number; ventas: number; ticketPromedio: number; pacientes: number; desde: string | null; hasta: string | null };
  gasto: { total: number; anuncios: number; conversaciones: number; desde: string | null; hasta: string | null; roasGlobal: number | null };
  atribucion: { conAnuncio: number; sinAnuncio: number; ventasConAnuncio: number; ventasSinAnuncio: number; porcentajeCubierto: number; ventasSinPaciente: number; montoSinPaciente: number;
    ventasPorHalory?: number; montoPorHalory?: number; porHaloryDisponible?: boolean };
  roas: RoasRow[];
  porMes: { mes: string; ingreso: number; ventas: number; dePauta: number }[];
  porOrigen: { origen: string; ingreso: number; ventas: number; ticket: number }[];
  porPersona: { persona: string; ingreso: number; ventas: number; pacientes: number }[];
  topConceptos: { concepto: string; ingreso: number; ventas: number }[];
  avisos: { empatePorNombre: string; causalidad: string; porHalory?: string };
  noCalculado?: string[];
}

interface AnuncioHalory {
  id: string | null; titulo: string | null; tipo: string | null; url: string | null; imagen: string | null;
  conversaciones: number; personas: number; canales: string[];
  desde: string; hasta: string; cruzableConGasto: boolean;
}
interface AnunciosResp {
  sinDatos?: boolean; motivo?: string; queHacer?: string;
  anuncios: AnuncioHalory[];
  serie?: { semana: string; conversaciones: number; conAnuncio: number }[];
  resumen: { conversacionesLeidas: number; conOrigen: number; sinOrigen: number; coberturaPct: number; anunciosDistintos: number; sinIdDeMeta: number };
  truncado: boolean; nota: string;
}

export function Marketing() {
  // Dos secciones (Administración → Permisos): "Marketing: dinero y pauta" y "Marketing: galería". Lo de siempre: una
  // cuenta de admin las dos; marketing sólo la galería (el dinero —ventas, ROAS— no le toca). Con una sola, sin pestañas.
  const { puede } = usePermisos();
  const conDinero = puede('marketing');
  const conGaleria = puede('galeria');
  const ambas = conDinero && conGaleria;
  const sinDinero = !conDinero; // sin la de dinero: ni la pide ni la enseña
  const [tab, setTabEstado] = useState<'dinero' | 'galeria'>(() => (!conDinero || (conGaleria && new URLSearchParams(window.location.search).get('tab') === 'galeria') ? 'galeria' : 'dinero'));
  const setTab = (t: 'dinero' | 'galeria') => { setTabEstado(t); const u = new URL(window.location.href); if (t === 'galeria') u.searchParams.set('tab', 'galeria'); else u.searchParams.delete('tab'); window.history.replaceState(null, '', u.toString()); };
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState(usePeriodoInicial());
  const [ads, setAds] = useState<AnunciosResp | null>(null);

  const load = (per = periodo, silent = false) => {
    if (sinDinero) return Promise.resolve();
    if (!silent) setD(null);
    const qs = periodoQS(per);
    return api<Resp>('marketing' + (qs ? `?${qs}` : '')).then(setD).catch((e) => setError(e.message));
  };
  // Los anuncios de Halory NO dependen del periodo ni de que haya dinero
  // cargado: es lo único que se puede enseñar antes de que alguien suba los dos
  // archivos, y contesta "¿qué anuncios están trayendo gente?" hoy mismo.
  const loadAds = () => { if (!sinDinero) api<AnunciosResp>('marketing/anuncios').then(setAds).catch(() => {}); };
  useEffect(() => { load(); loadAds(); }, []);
  useLive(['config', 'patient'], () => load(periodo, true));

  const axis = { fontSize: 11, fill: '#6b6b6b' };

  if (tab === 'galeria') {
    return (
      <div>
        <TopBar />
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '18px 16px 48px' }}>
          <h1 style={{ margin: '4px 2px 6px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Marketing</h1>
          {ambas && <TabsMk tab={tab} setTab={setTab} />}
          <GaleriaContenido />
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar />
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 6px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Marketing</h1>
        {ambas && <TabsMk tab={tab} setTab={setTab} />}
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Dinero <strong>real</strong> cobrado contra gasto <strong>real</strong> de pauta. No es la estimación a precio de lista de Analítica.
        </p>

        {/* El selector de periodo filtra los COBROS cargados: sin datos no hay
            nada que filtrar y solo confunde ("los filtros no hacen nada"). */}
        {d && !d.sinDatos && (
          <div style={{ margin: '0 2px 14px' }}>
            <PeriodPicker value={periodo} onChange={(v) => { setPeriodo(v); load(v); }} />
          </div>
        )}

        {error && <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{error}</div>}
        {!d && !error && <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Calculando…</div>}

        {d?.sinDatos && (
          <div className="card" style={{ padding: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Todavía no hay dinero cargado</div>
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              {d.motivo} {d.queHacer}
            </div>
            <Link to="/config" className="btn-ghost" style={{ display: 'inline-block', marginTop: 12, textDecoration: 'none' }}>Ir a Salud de datos</Link>
          </div>
        )}

        {/* Va SIEMPRE, con dinero o sin él: son dos preguntas distintas.
            "¿Qué anuncios traen gente?" se puede contestar hoy con lo que Halory
            ya sabe; "¿cuál se paga solo?" necesita los dos archivos. */}
        {ads && <AnunciosDeHalory a={ads} />}

        {d && !d.sinDatos && d.ingreso.ventas === 0 && (
          <div className="card" style={{ padding: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No hay ventas en este periodo</div>
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              No hay cobros en <strong>{d.periodo.label}</strong>. El dinero sale de lo que cobra recepción y de la
              tienda de la app: se ve aquí en cuanto haya cobros en el periodo elegido.
            </div>
          </div>
        )}

        {d && !d.sinDatos && d.ingreso.ventas > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 14 }}>
            {/* ---- Lo que entró y lo que costó ------------------------------ */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 10 }}>
              <Kpi label="Ingreso cobrado" v={money(d.ingreso.total)} sub={`${d.ingreso.ventas} ventas`} fuerte />
              <Kpi label="Gasto en pauta" v={d.gasto.total > 0 ? money(d.gasto.total) : '—'} sub={d.gasto.total > 0 ? `${d.gasto.anuncios} anuncios` : 'sin export cargado'} />
              <Kpi label="Retorno global" v={d.gasto.roasGlobal != null ? `${d.gasto.roasGlobal}x` : '—'} sub="ingreso ÷ pauta" />
              <Kpi label="Ticket promedio" v={money(d.ingreso.ticketPromedio)} sub={`${d.ingreso.pacientes} pacientes`} />
            </div>

            {/* ---- El hueco, antes que cualquier conclusión ------------------ */}
            <Cobertura a={d.atribucion} />

            {/* ---- ROAS por anuncio ----------------------------------------- */}
            {d.roas.length > 0 && (
              <div className="card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--gold-dark)' }}>Anuncio por anuncio</h3>
                  <span className="muted" style={{ fontSize: 11 }}>{d.roas.length} anuncios</span>
                </div>
                <div className="muted" style={{ fontSize: 12.5, marginBottom: 10, lineHeight: 1.55 }}>
                  Un anuncio con gasto y <strong>sin</strong> ingreso también aparece: es el renglón más caro de esta tabla
                  y el que se pierde cuando sólo se listan los que vendieron.
                </div>
                <TablaRoas filas={d.roas} />
              </div>
            )}

            {/* ---- Mes a mes ------------------------------------------------ */}
            {d.porMes.length > 1 && (
              <ChartCard
                title="¿De cuánto dinero de cada mes sabemos qué anuncio lo trajo?"
                hint="barra = total · línea = con anuncio"
                como="La barra es todo lo cobrado ese mes; la línea, la parte que se sabe de qué anuncio vino."
                data={d.porMes}
                foot="La distancia entre la barra y la línea es el dinero que entró sin que sepamos por qué. Si esa distancia no baja, el problema no es la pauta: es el registro."
                render={(h) => (
                  <ResponsiveContainer width="100%" height={h}>
                    <ComposedChart data={d.porMes} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke="#efece6" vertical={false} />
                      <XAxis dataKey="mes" tick={axis} axisLine={false} tickLine={false} />
                      <YAxis tick={axis} tickFormatter={(v) => dineroEje(Number(v))} axisLine={false} tickLine={false} width={48} />
                      <Tooltip formatter={(v: any, n: any) => [money(Number(v)), n === 'dePauta' ? 'Atribuido a pauta' : 'Ingreso total']} />
                      <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: 'var(--ink)' }}>{v === 'dePauta' ? 'Con anuncio' : 'Ingreso total'}</span>} />
                      <Bar dataKey="ingreso" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false} />
                      <Line dataKey="dePauta" stroke={GOLD_DARK} strokeWidth={2} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 14 }}>
              {/* ---- Primera vez vs subsecuente --------------------------- */}
              <ChartCard
                title="¿El dinero lo traen pacientes nuevos o los de siempre?"
                datos="mk-origen"
                data={d.porOrigen}
                como="PV = primera vez en la clínica · SUB = ya había venido. Con su % del dinero."
                foot="Si casi todo es subsecuente, el negocio lo sostiene quien ya era paciente, no la pauta. Son dos problemas distintos y piden dos presupuestos distintos. «Sin dato» es una venta que no dice si fue primera vez (la tienda de la app)."
                render={() => (
                  <Barras filas={d.porOrigen.map((o) => ({ clave: o.origen, nombre: o.origen === 'PV' ? 'Primera vez (PV)' : o.origen === 'SUB' ? 'Ya había venido (SUB)' : o.origen === '(sin dato)' ? 'Sin dato' : o.origen, n: o.ingreso, extra: `· ${o.ventas} ventas`, color: o.origen === 'PV' ? CATEGORICO[0] : o.origen === 'SUB' ? CATEGORICO[2] : NEUTRO }))}
                    formato={(f, tot) => `${money(f.n)} · ${porcentaje(f.n, tot)}`} datos="mk-origen" />
                )}
              />

              {/* ---- Dinero por buyer persona ------------------------------ */}
              <ChartCard
                title="¿Qué perfil deja más dinero?"
                datos="mk-perfil"
                data={d.porPersona}
                como="Lo cobrado a cada perfil (el que calcula el HUB con quién es cada persona) y su % del dinero."
                foot="Se miden por dinero, no por cantidad de citas: un perfil puede traer el triple de leads y la mitad de ingreso, y esas dos lecturas piden decisiones opuestas. «Sin perfil» es quien no tiene con qué decidirlo (falta su sexo o su edad); «Sin expediente», dinero que no se empató con nadie."
                render={() => (
                  <Barras filas={d.porPersona.map((x) => ({ clave: x.persona, nombre: x.persona === '(sin datos)' ? 'Sin perfil (faltan datos)' : x.persona === '(sin expediente)' ? 'Sin expediente' : x.persona, n: x.ingreso, extra: `· ${x.pacientes} ${x.pacientes === 1 ? 'paciente' : 'pacientes'}`, color: COLOR_PERSONA[x.persona] || NEUTRO }))}
                    formato={(f, tot) => `${money(f.n)} · ${porcentaje(f.n, tot)}`} datos="mk-perfil" />
                )}
              />
            </div>

            {/* ---- Qué se vende -------------------------------------------- */}
            {d.topConceptos.length > 0 && (
              <ChartCard
                title="¿Qué deja más dinero?"
                datos="mk-conceptos"
                data={d.topConceptos}
                como="Agrupado por el texto tal cual se capturó. Una línea puede ser un paquete de varias cosas: no se parte, porque repartir el monto entre las partes sería inventarlo."
                render={() => (
                  <Barras filas={d.topConceptos.map((c) => ({ clave: c.concepto, nombre: c.concepto, n: c.ingreso, extra: `· ${c.ventas}×` }))} total={d.ingreso.total}
                    formato={(f, tot) => `${money(f.n)} · ${porcentaje(f.n, tot)}`} datos="mk-conceptos" />
                )}
              />
            )}

            <div className="muted" style={{ fontSize: 12, lineHeight: 1.6, padding: '0 2px' }}>
              <div>· {d.avisos.empatePorNombre}</div>
              <div>· {d.avisos.causalidad}</div>
              {d.avisos.porHalory && <div>· {d.avisos.porHalory}</div>}
              {d.ingreso.desde && <div>· Ventas cargadas: del {diaTxt(d.ingreso.desde)} al {diaTxt(d.ingreso.hasta)}. {d.gasto.desde ? `Gasto cargado: del ${diaTxt(d.gasto.desde)} al ${diaTxt(d.gasto.hasta)}.` : 'Sin gasto de pauta cargado.'}</div>}
              <div data-pauta-cadencia>· El gasto de pauta llega solo de Halory (la cuenta publicitaria conectada allá): cada hora se traen los últimos 2 días, que Meta todavía reajusta, y cada noche los últimos 28. Traerlo otra vez corrige, no suma.</div>
              {d.noCalculado?.length ? <div style={{ color: 'var(--warning)' }}>· No se pudieron calcular {d.noCalculado.length} panel(es): {d.noCalculado.join(' · ')}</div> : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * De qué anuncios está llegando la gente, según Halory.
 *
 * Esto NO es dinero y la pantalla no puede dejar que se lea como si lo fuera:
 * son conversaciones iniciadas. Un anuncio con 80 chats y ninguna paciente es
 * caro, no bueno — así que el encabezado dice "conversaciones", no "resultados",
 * y no hay ninguna columna que parezca un retorno.
 *
 * El ID de Meta se muestra completo porque es la llave para cruzar con la
 * facturación: dos anuncios pueden llamarse igual, y por nombre se sumarían en
 * una fila sin que nadie lo note. Los que no traen id se marcan.
 */
function AnunciosDeHalory({ a }: { a: AnunciosResp }) {
  const [abierto, setAbierto] = useState(false);
  const [creativo, setCreativo] = useState<AnuncioHalory | null>(null);
  if (a.sinDatos) {
    return (
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Anuncios según Halory</div>
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>{a.motivo} {a.queHacer}</div>
      </div>
    );
  }
  const r = a.resumen;
  const vista = abierto ? a.anuncios : a.anuncios.slice(0, 8);
  return (
    <div className="card" style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>De qué anuncios está llegando la gente</div>
        <span className="badge" style={{ background: 'var(--gold-light)', color: 'var(--gold-dark)' }}>{r.anunciosDistintos} anuncios</span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
        {a.nota} Lo sabe Halory porque Meta manda el anuncio pegado al primer mensaje de un click-to-WhatsApp.
      </div>

      {/* La cobertura antes de la tabla: si sólo una fracción de las
          conversaciones trae anuncio, este ranking describe ese pedacito y no
          la pauta. Es el mismo cuidado que el bloque de atribución del dinero. */}
      <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        <strong style={{ color: r.coberturaPct >= 50 ? 'var(--gold-dark)' : 'var(--warning)' }}>{r.coberturaPct}%</strong>
        {' '}de {r.conversacionesLeidas} conversaciones traen anuncio ({r.sinOrigen} entraron por otro lado: mensaje directo, perfil, orgánico).
        {r.sinIdDeMeta > 0 && <> {r.sinIdDeMeta} anuncio(s) sin id de Meta — esos no se pueden cruzar con el gasto.</>}
        {a.truncado && <> El barrido de Halory tocó su tope, así que estos conteos son un <strong>piso</strong>.</>}
      </div>

      {/* La primera gráfica que esta pestaña puede enseñar sin subir NINGÚN
          archivo: cada conversación trae su fecha. Barras = todas las
          conversaciones; línea = las que traen anuncio de origen. */}
      {(a.serie || []).length > 1 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Conversaciones por semana</div>
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={a.serie} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
              <XAxis dataKey="semana" tick={{ fontSize: 10, fill: '#6b6b6b' }}
                tickFormatter={(v: string) => new Date(v + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} />
              <YAxis tick={{ fontSize: 10, fill: '#6b6b6b' }} allowDecimals={false} />
              {/* `semana` es un DÍA ("AAAA-MM-DD", el lunes): sin la hora, new Date() lo lee como medianoche UTC y en CDMX salía el domingo anterior. */}
              <Tooltip labelFormatter={(v: string) => 'Semana del ' + new Date(v + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })} />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v: any) => <span style={{ color: 'var(--ink)' }}>{v}</span>} />
              <Bar dataKey="conversaciones" name="Conversaciones" fill={GOLD_LIGHT} radius={[4, 4, 0, 0]} />
              <Line dataKey="conAnuncio" name="Con anuncio de origen" stroke={GOLD_DARK} strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {a.anuncios.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>Ninguna conversación trae anuncio de origen todavía.</div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6b6b6b' }}>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Anuncio</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Personas</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Periodo</th>
              </tr>
            </thead>
            <tbody>
              {vista.map((x, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 8px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {/* Miniatura REAL del creativo: Meta la manda en el referral del
                          click-to-WhatsApp y Halory la guarda. Clic = verla en grande.
                          Es un link al CDN de Meta: si caducó, la miniatura se oculta
                          sola (onError) y queda el título con su link. */}
                      {x.imagen ? (
                        <img src={x.imagen} alt="" loading="lazy"
                          onClick={() => setCreativo(x)}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: '1px solid var(--border)', flexShrink: 0 }} />
                      ) : (
                        <span title="Meta no mandó miniatura para este anuncio (pasa con videos, historias y formatos viejos) — el link al anuncio sigue sirviendo"
                          style={{ width: 44, height: 44, borderRadius: 8, border: '1px dashed var(--border)', display: 'inline-flex',
                                   alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#9ca3af', textAlign: 'center', flexShrink: 0, lineHeight: 1.2 }}>
                          sin img</span>
                      )}
                      <div>
                        <div style={{ fontWeight: 500 }}>{x.titulo || '(sin título)'}
                          {x.url && <a href={x.url} target="_blank" rel="noreferrer"
                            style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--gold-dark)',
                                     border: '1px solid var(--gold)', borderRadius: 8, padding: '1px 8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                            Ver anuncio ↗</a>}
                        </div>
                        <div className="muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                          {x.id ? `id ${x.id}` : 'sin id de Meta'}{x.tipo ? ` · ${x.tipo}` : ''}{x.canales.length ? ` · ${x.canales.join(', ')}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Personas ≠ chats solo cuando alguien volvió a escribir (chat
                      nuevo tras cerrarse el anterior) o entró por dos canales. Si son
                      iguales, enseñar dos columnas idénticas es ruido: el extra sale
                      como "+N repetidos" solo cuando existe. */}
                  <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {x.personas || x.conversaciones}
                    {x.conversaciones > x.personas && x.personas > 0 && (
                      <span className="muted" style={{ fontWeight: 400, fontSize: 11 }} title="La misma persona abrió más de un chat (volvió a escribir después, o entró por dos canales)"> +{x.conversaciones - x.personas} rep.</span>
                    )}
                  </td>
                  <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }} className="muted">{diaTxt(x.desde)} → {diaTxt(x.hasta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {a.anuncios.length > 8 && (
            <button className="btn-ghost" style={{ marginTop: 8, fontSize: 12 }} onClick={() => setAbierto(!abierto)}>
              {abierto ? 'Ver menos' : `Ver los ${a.anuncios.length}`}
            </button>
          )}
        </div>
      )}

      {creativo && (
        <div onClick={() => setCreativo(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.92)', zIndex: 60,
                   display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16 }}>
          <img src={creativo.imagen || ''} alt={creativo.titulo || ''} onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '72vh', borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
          <div style={{ color: '#e5e7eb', fontSize: 14, textAlign: 'center', maxWidth: 640 }}>
            <strong>{creativo.titulo || '(sin título)'}</strong>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 3 }}>
              {creativo.conversaciones} conversaciones · {diaTxt(creativo.desde)} → {diaTxt(creativo.hasta)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }} onClick={(e) => e.stopPropagation()}>
            {creativo.url && <a href={creativo.url} target="_blank" rel="noreferrer"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.55)', borderRadius: 10, padding: '8px 18px', fontSize: 14, textDecoration: 'none' }}>
              Abrir en Meta ↗</a>}
            <button onClick={() => setCreativo(null)}
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.55)', borderRadius: 10, padding: '8px 18px', fontSize: 14, cursor: 'pointer' }}>
              Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, v, sub, fuerte }: { label: string; v: string; sub?: string; fuerte?: boolean }) {
  return (
    <div className="card" style={{ padding: '12px 14px', minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      {/* En celular el monto se achica en vez de pegarse al borde de la tarjeta ("$4,711,590"). */}
      <div style={{ fontSize: fuerte ? 'clamp(19px, 5.4vw, 25px)' : 'clamp(18px, 5vw, 22px)', fontWeight: 700, color: fuerte ? 'var(--gold-dark)' : undefined, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={v}>{v}</div>
      {sub && <div className="muted" style={{ fontSize: 11.5 }}>{sub}</div>}
    </div>
  );
}

/**
 * La cobertura de atribución, arriba y en grande.
 *
 * Es lo primero que hay que ver porque cambia cómo se lee todo lo de abajo. Con
 * 11% de cobertura, "el anuncio X trajo $X" no es un resultado: es una muestra
 * de conveniencia. La barra hace visible de un golpe qué parte del dinero está
 * explicada y qué parte no.
 */
function Cobertura({ a }: { a: Resp['atribucion'] }) {
  const pct = a.porcentajeCubierto;
  const flojo = pct < 50;
  return (
    <div className="card" style={{ padding: '14px 16px', background: flojo ? 'var(--warning-bg)' : undefined, border: flojo ? 'none' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: flojo ? 'var(--warning)' : 'var(--gold-dark)' }}>
          De cuánto dinero sabemos de dónde vino
        </h3>
        <strong style={{ fontSize: 20, color: flojo ? 'var(--warning)' : 'var(--gold-dark)' }}>{pct}%</strong>
      </div>

      <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', margin: '10px 0 8px', background: '#e8e3da' }}>
        <div style={{ width: `${pct}%`, background: GOLD_DARK }} title={`Con anuncio: ${money(a.conAnuncio)}`} />
        <div style={{ flex: 1, background: '#d6cfc2' }} title={`Sin anuncio: ${money(a.sinAnuncio)}`} />
      </div>

      <div style={{ fontSize: 13, lineHeight: 1.65 }}>
        <strong>{money(a.conAnuncio)}</strong> tiene un anuncio detrás ({a.ventasConAnuncio} ventas).
        {(a.ventasPorHalory ?? 0) > 0 && (
          <span data-atrib-halory> De eso, {money(a.montoPorHalory || 0)} ({a.ventasPorHalory} ventas) se sabe por el anuncio con el que la persona llegó a Halory.</span>
        )}
        {' '}<strong>{money(a.sinAnuncio)}</strong> no ({a.ventasSinAnuncio} ventas) — entró, pero no se sabe por dónde llegó.
        {flojo && (
          <div style={{ marginTop: 6 }}>
            Con esta cobertura, el retorno por anuncio de abajo se calcula sobre {pct}% del dinero.
            Sirve para comparar anuncios <em>entre sí</em>; no para decidir cuánto vale la pauta en total.
            {a.porHaloryDisponible
              ? 'Crece sola a medida que llega gente por anuncios a Halory: sus compras en recepción se ligan a ese anuncio.'
              : 'El arreglo no es analítico: es capturar el origen al cobrar, o traer el gasto desde Halory (liga cada venta al anuncio por el que llegó la persona).'}
          </div>
        )}
        {a.ventasSinPaciente > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {a.ventasSinPaciente} ventas ({money(a.montoSinPaciente)}) no empataron con ningún expediente de la base —
            teléfono ausente o distinto al del expediente. Cuentan en el ingreso; no cuentan en LTV ni en recompra.
          </div>
        )}
      </div>
    </div>
  );
}

/** Tabla de retorno por anuncio, ordenable. Sin gráfica: son 20+ nombres largos
 *  y una barra con etiquetas ilegibles se ve muy bien y no se puede usar. */
function TablaRoas({ filas }: { filas: RoasRow[] }) {
  const [orden, setOrden] = useState<'ingreso' | 'gasto' | 'roas'>('ingreso');
  const orden2 = [...filas].sort((a, b) => {
    if (orden === 'roas') {
      // Los que no tienen ROAS (sin gasto cargado) van al final: no son "cero
      // retorno", son "no sabemos", y mezclarlos con los malos es un error caro.
      if (a.roas == null && b.roas == null) return b.ingreso - a.ingreso;
      if (a.roas == null) return 1;
      if (b.roas == null) return -1;
      return b.roas - a.roas;
    }
    return (b[orden] || 0) - (a[orden] || 0);
  });

  const th: React.CSSProperties = { textAlign: 'right', padding: '6px 8px', fontSize: 11.5, color: '#6b6b6b', fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer' };
  const td: React.CSSProperties = { textAlign: 'right', padding: '7px 8px', fontSize: 13, whiteSpace: 'nowrap' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...th, textAlign: 'left', cursor: 'default' }}>Anuncio</th>
            <th style={th} onClick={() => setOrden('gasto')}>Gasto {orden === 'gasto' ? '▾' : ''}</th>
            <th style={th} onClick={() => setOrden('ingreso')}>Ingreso {orden === 'ingreso' ? '▾' : ''}</th>
            <th style={th}>Ventas</th>
            <th style={th} onClick={() => setOrden('roas')}>Retorno {orden === 'roas' ? '▾' : ''}</th>
          </tr>
        </thead>
        <tbody>
          {orden2.map((f) => {
            // Verde a partir de 3x: por debajo de eso, en estética, el margen
            // después de producto y médico se lo come la propia venta.
            const color = f.roas == null ? undefined : f.roas >= 3 ? VERDE : f.roas >= 1 ? GOLD_DARK : ROJO;
            return (
              <tr key={f.anuncio} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '7px 8px', fontSize: 13, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${f.anuncio}${f.campana ? ' · ' + f.campana : ''}`}>
                  {f.anuncio}
                  {f.campana && <div className="muted" style={{ fontSize: 11 }}>{f.campana}</div>}
                </td>
                <td style={td}>{f.gasto > 0 ? money(f.gasto) : <span className="muted">—</span>}</td>
                <td style={td}>{f.ingreso > 0 ? money(f.ingreso) : <span className="muted">—</span>}</td>
                <td style={td}>{f.ventas || <span className="muted">—</span>}</td>
                <td style={{ ...td, fontWeight: 600, color }}>
                  {f.roas != null ? `${f.roas}x` : <span className="muted" style={{ fontWeight: 400 }}>sin gasto</span>}
                  {f.costoPorVenta != null && <div className="muted" style={{ fontSize: 11, fontWeight: 400 }}>{money(f.costoPorVenta)}/venta</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TabsMk({ tab, setTab }: { tab: 'dinero' | 'galeria'; setTab: (t: 'dinero' | 'galeria') => void }) {
  return <Pestanas items={[['dinero', 'Dinero y pauta'], ['galeria', 'Galería']] as const} valor={tab} onCambio={setTab} etiqueta="Secciones de Marketing" estilo={{ margin: '0 2px 12px' }} />;
}
